import "pages/finance/filters/shared.scss";

import Button from "components/Button/Button.tsx";
import type { OptionType } from "components/inputs";
import FinanceFilterDropdown, {
  FinanceFilterButton,
} from "pages/finance/filters/components/FinanceFilterDropdown/FinanceFilterDropdown";
import FinanceFilterPills from "pages/finance/filters/components/FinanceFilterPills/FinanceFilterPills";
import type { FinanceFilters } from "pages/finance/filters/types";
import ReportTypeHeaderSelector, {
  REPORT_TYPE_LABELS,
} from "pages/finance/filters/components/ReportTypeHeaderSelector/ReportTypeHeaderSelector";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { SavedView } from "services/saved-view-api";
import { useQuery } from "utils/hooks";

import FinanceHeaderActions from "../../../pages/finance/components/FinanceHeaderActions";
import { useReportActions } from "../../../pages/finance/contexts/ReportActionsContext";

type FinanceFilterContainerProps = {
  currentPreset?: SavedView | null;
  onLoadPreset?: (preset: SavedView) => void;
  reportStatus?: React.ReactNode;
};

export const hasActiveFinanceFilters = (filters: FinanceFilters) => {
  return Object.entries(filters).some(([key, value]) => {
    if (key === "report_type") return false;
    if (key === "client_search_term") return false;
    if (key === "client_service_map") return false;
    if (key === "order_by") return false;

    // section 111 defaults to hiding invoiced rows, so only count this when
    // the user explicitly disables that default.
    if (key === "hide_invoiced") return value === false;

    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "object" && value !== null)
      return Object.keys(value).length > 0;

    return value != null;
  });
};

const FinanceFilterContainer: React.FC<FinanceFilterContainerProps> = ({
  currentPreset = null,
  onLoadPreset,
  reportStatus,
}) => {
  const { state } = useReportActions();
  const { onViewReport, isLoadingReportData, onResetReport, showReportTable } =
    state;
  const navigate = useNavigate();
  const searchParams = useQuery();

  // handle loading a preset - navigate to the preset's URL
  const handleLoadPreset = (preset: SavedView) => {
    if (onLoadPreset) {
      onLoadPreset(preset);
    } else {
      // default behavior: navigate to the preset URL
      navigate(preset.url_path);
    }
  };

  // get report type from URL, default to INVOICE_LIST if not present
  const reportTypeValue = searchParams.get("reportType") || "INVOICE_LIST";
  const reportType = {
    label:
      REPORT_TYPE_LABELS[reportTypeValue] || reportTypeValue.replace(/_/g, " "),
    value: reportTypeValue,
  } as OptionType;

  // handle report type change
  const handleReportTypeChange = (option: OptionType | null) => {
    if (!option) {
      return; // should never happen since isClearable is false
    }

    const params = new URLSearchParams();
    params.set("reportType", option.value.toString());
    navigate(`${window.location.pathname}?${params.toString()}`, {
      replace: true,
    });
  };

  // generate shareable URL for presets
  const currentUrl = window.location.href;

  return (
    <div className="finance-filter-header-container">
      {/* section 1: report type + generate button (1/4 width) */}
      <div className="header-section-left">
        <ReportTypeHeaderSelector
          value={reportType}
          onChange={handleReportTypeChange}
        />
        {onViewReport && (
          <Button
            onClick={onViewReport}
            disabled={isLoadingReportData}
            className="generate-report-button"
          >
            <span className="material-icons">table_view</span>
            Generate Report
          </Button>
        )}
        {onResetReport && (
          <Button
            onClick={onResetReport}
            disabled={!showReportTable}
            className="reset-report-button"
          >
            <span className="material-icons">refresh</span>
            Reset
          </Button>
        )}
      </div>

      {/* section 2: report status (2/4 width) */}
      <div className="header-section-center">{reportStatus}</div>

      {/* section 3: export, save, load trio (1/4 width) */}
      <div className="header-section-right">
        <FinanceHeaderActions
          currentUrl={currentUrl}
          onLoadPreset={handleLoadPreset}
          currentPreset={currentPreset}
        />
      </div>
    </div>
  );
};

// Export filter bar component for use in action bar
export const FinanceFilterBar: React.FC<{
  filters: FinanceFilters;
  onRemoveFilter: (key: keyof FinanceFilters) => void;
  onClearFilters: () => void;
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onUpdateFilter: (key: keyof FinanceFilters, value: unknown) => void;
  onUpdateFilters: (updates: Partial<FinanceFilters>) => void;
  clientOptions: OptionType[];
  fileOptions: OptionType[];
  invoiceOptions: OptionType[];
  isClientsFetching: boolean;
  isFilesFetching: boolean;
  isInvoicesFetching: boolean;
  onClientSearch: (term: string) => void;
  onFileSearch: (term: string) => void;
  onInvoiceSearch: (term: string) => void;
  isRulesExpanded: boolean;
  onToggleRules: () => void;
  showRules: boolean;
}> = ({
  filters,
  onRemoveFilter,
  onClearFilters,
  isDropdownOpen,
  onToggleDropdown,
  onUpdateFilter,
  onUpdateFilters,
  clientOptions,
  fileOptions,
  invoiceOptions,
  isClientsFetching,
  isFilesFetching,
  isInvoicesFetching,
  onClientSearch,
  onFileSearch,
  onInvoiceSearch,
  isRulesExpanded,
  onToggleRules,
  showRules,
}) => {
  const hasActiveFilters = hasActiveFinanceFilters(filters);

  const hasActiveRules =
    filters.client_service_map &&
    Object.keys(filters.client_service_map).length > 0;

  return (
    <div className="finance-filter-bar-wrapper">
      <div className="finance-filter-bar">
        <div className="filter-bar-left">
          <FinanceFilterButton
            onClick={onToggleDropdown}
            isOpen={isDropdownOpen}
            hasActiveFilters={hasActiveFilters}
          />

          <FinanceFilterPills
            filters={filters}
            onRemoveFilter={onRemoveFilter}
          />
        </div>

        <div className="filter-bar-right">
          {hasActiveFilters && (
            <button
              className="clear-filters-button"
              onClick={onClearFilters}
              type="button"
            >
              <span className="material-icons">close</span>
              <span>Clear Filters</span>
            </button>
          )}

          {showRules && (
            <button
              className={`finance-filter-button ${isRulesExpanded || hasActiveRules ? "open" : ""}`}
              onClick={onToggleRules}
              type="button"
            >
              <span className="button-text">
                Advanced{" "}
                {hasActiveRules &&
                  `(${Object.keys(filters.client_service_map).length})`}
              </span>
              <span className="material-icons">
                {isRulesExpanded ? "expand_less" : "expand_more"}
              </span>
            </button>
          )}
        </div>
      </div>

      <FinanceFilterDropdown
        isOpen={isDropdownOpen}
        onClose={() => onToggleDropdown()}
        filters={filters}
        onUpdateFilter={onUpdateFilter}
        onUpdateFilters={onUpdateFilters}
        clientOptions={clientOptions}
        fileOptions={fileOptions}
        invoiceOptions={invoiceOptions}
        isClientsFetching={isClientsFetching}
        isFilesFetching={isFilesFetching}
        isInvoicesFetching={isInvoicesFetching}
        onClientSearch={onClientSearch}
        onFileSearch={onFileSearch}
        onInvoiceSearch={onInvoiceSearch}
      />
    </div>
  );
};

export default FinanceFilterContainer;
